import {USER_KEYS} from "@/api/userApi";
import WhiteTickIcon from "@/assets/FFFFFF_tick_icon.svg?react";
import Loader from "@/components/Common/kpi/Loader";
import {client} from "@/httpClient";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import useRegistrationStore, {EmailSchema} from "@/store/registration";
import {useMutation} from "@tanstack/react-query";
import {type FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce} from "react-use";

const RegistrationEmailField: FC = () => {
  const {t} = useTranslation();
  const email = useRegistrationStore(state => state.email);
  const isPrefilled = useRegistrationStore(state => state.isEmailPrefilled);
  const isEmailValidated = useRegistrationStore(state => state.isEmailValidated);

  const [emailState, setEmailState] = useState(email);
  useDebounce(
    () => {
      if (isPrefilled) {
        setValue("isEmailValidated", true);
        return;
      }

      setValue("email", emailState);
      if (emailState !== "" && validateSchema(EmailSchema)) {
        checkIfEmailExists(emailState);
      }
    },
    1000,
    [emailState],
  );

  const setValue = useRegistrationStore(state => state.setValue);
  const setError = useRegistrationStore(state => state.setError);
  const validateSchema = useRegistrationStore(state => state.validateSchema);
  const {mutate: checkIfEmailExists, isPending} = useMutation({
    mutationKey: USER_KEYS.mUserEmail(email),
    mutationFn: (email: string) => client.get(USER_KEYS.mUserEmail(email)[0]),
    onSuccess: response => {
      setValue("isEmailValidated", !response.data.exists);
      setError("email", response.data.exists ? "emailAlreadyExists" : "");
    },
  });

  return (
    <RegistrationInputField
      storeKey="email"
      label={t("email")}
      placeholder={t("yourEmailAddress")}
      type="email"
      disabled={isPrefilled}
      value={emailState}
      onChange={value => {
        setEmailState(value);
        setValue("isEmailValidated", false);
        setError("email", "");
      }}
      icon={
        isPrefilled ? null : isEmailValidated ? (
          <div className={"absolute top-0 right-0 bottom-0 mr-3 flex h-full items-center justify-center p-2"}>
            <WhiteTickIcon className="border-success-light-green bg-success-herbal size-6 rounded-full border p-1" />
          </div>
        ) : (
          isPending && (
            <Loader
              styles={{
                loaderClassName: "h-[22px] w-[22px]",
                containerClassName:
                  "absolute bottom-0 right-0 top-0 h-full pr-5 pl-1.5 flex items-center justify-center",
              }}
            />
          )
        )
      }
    />
  );
};

export default RegistrationEmailField;
